import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Optional, Output, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { AGUCrumbService, AGUToastService, LatestRequest } from 'agu_app_shell';
import { ItemKind, ResearchApplyLink, ResearchJobDetail } from '../../models/research.models';
import { ResearchApiService } from '../../services/research-api.service';
import { MemberItemStateChange, MemberItemStateService } from '../../services/member-item-state.service';
import { ExplorerRaHostAdapter } from '../../ra-explorer-host.adapter';
import { RaCompositionState } from '../../ra-composition.state';
import { RA_MODULE_KEYS } from '../../ra-module-keys';
import { DetailCrumb } from '../shared/detail-crumb';
import { FormatJobMeta, FormatJobPostedFact, FormatSalaryLine } from './job-card.component';
import { BenefitChips, CompanyTypeLabel, Initials } from './job-feed-fields';
import { DescribeError } from '../shared/describe-error';
import { LoadFailures } from '../shared/load-failures';
import { LISTING_GONE_COPY, PageErrorCopy, StatePanelCopy } from '../shared/state-copy';
import { PortalConfigService } from '../../services/portal-config.service';
import { SplitParagraphs } from '../shared/paragraphs';

const JOB_KIND: ItemKind = 'job';

/** How many benefit chips show before the "+N more" button (global-constraints "Benefits"). */
const BENEFITS_SHOWN = 10;

/** The company-description heading: "About {company}", or "About the employer" when the feed gave no (or a blank) company name. */
export function AboutCompanyHeading(companyName: string | null): string {
    const name = companyName?.trim() ?? '';
    return name.length > 0 ? `About ${name}` : 'About the employer';
}

/**
 * The Work location tile's value (detail.html:393's `Work location`). The feed carries only the
 * `remote` flag, so the tile says what that flag says — in the rail's own Work location labels
 * (`job-filters.component.ts`, `Remote`/`On-site`) — and nothing it cannot know (no "hybrid").
 */
export function FormatWorkLocation(remote: boolean | null): string {
    if (remote === true) return 'Remote';
    if (remote === false) return 'On-site';
    return 'Not stated';
}

/**
 * The salary tile's label: the mockup's `Est. salary` (detail.html:393) when the figure IS an
 * estimate, plain `Salary` when the employer stated it — the card's `Est. salary` caption follows
 * the same flag (job-card.component.html), so the two never disagree about one job.
 */
export function SalaryFactLabel(salaryIsEstimated: boolean | null): string {
    return salaryIsEstimated === true ? 'Est. salary' : 'Salary';
}

/** What a loaded (or not-found) job renders; computed once per load, off the zoneless render path. */
type JobView = Pick<
    JobDetailComponent,
    | 'job'
    | 'PostedLine'
    | 'SalaryLine'
    | 'SalaryLabel'
    | 'WorkLocationLine'
    | 'AboutHeading'
    | 'DescriptionParagraphs'
    | 'LogoUrl'
    | 'CompanyInitials'
    | 'CompanyTypeTag'
    | 'BenefitsAll'
    | 'BenefitsVisible'
    | 'BenefitsMore'
    | 'PrimaryApply'
    | 'OtherApply'
>;

/**
 * Job Finder's detail view: one job's full record, loaded by `JobID`. It
 * opens on the crumb trail (`DetailCrumb`): the first ancestor is where the
 * job was opened from (`AGUAppLocation.From`), else Job Finder's search —
 * same split as `GrantDetailComponent`.
 */
@Component({
    standalone: false,
    selector: 'ra-job-detail',
    templateUrl: './job-detail.component.html',
    styleUrl: './job-detail.component.scss',
})
/** The job detail view: loads the job for `JobID`, reloads when the ID changes and drops responses to superseded loads. */
export class JobDetailComponent implements OnInit, OnChanges, OnDestroy {
    @Input({ required: true }) JobID!: string;
    /** The host's verdict that this job is one of the member's recommendations (`JobFinderPageComponent.DetailIsRecommended`). */
    @Input() Recommended = false;
    /** The gone-listing panel's "See similar" (P/proto-states.js:121); the page navigates to its finder's All tab. */
    @Output() readonly SeeSimilar = new EventEmitter<void>();
    /** The gone-listing panel's "Back to Home" (P/proto-states.js:121); the page navigates. */
    @Output() readonly BackToHome = new EventEmitter<void>();
    /** The crumb trail that replaced the back pill: "{origin} / {title}" (P/detail.html:888). */
    readonly Crumb: DetailCrumb;

    job: ResearchJobDetail | null = null;
    loading = false;
    error: string | null = null;
    /** The error panel's foot, retry note and Contact support link (PD §5). */
    readonly failures = new LoadFailures();
    /** A record that is gone (P/proto-states.js:121) and a load that failed (state-copy.ts `PageErrorCopy`). */
    readonly GoneCopy: StatePanelCopy = LISTING_GONE_COPY;
    readonly ErrorCopy: StatePanelCopy = PageErrorCopy('this job');

    /**
     * Computed once, right after `Load()` resolves — not a template-bound
     * getter. `FormatJobPostedFact` defaults `now` to `new Date()`; a getter
     * would re-evaluate that on every change-detection pass (ui-best-
     * practices §7's "never bind time-dependent method calls in templates").
     * `OnItemStateChanged` only ever touches `itemState`, never `datePosted`,
     * so it never needs to recompute this.
     */
    PostedLine = 'Posted date not stated';

    /** Computed once after `Load()` (zoneless rule: no per-pass string splitting in a getter) — mirrors `GrantDetailComponent.DescriptionParagraphs`. */
    DescriptionParagraphs: string[] = [];

    /**
     * Logo/initials/type-chip, computed once after `Load()` — same zoneless
     * rule, and the same "one `error` event, no reload loop" contract as
     * `JobCardComponent.OnLogoError` (Review Focus 2).
     */
    LogoUrl: string | null = null;
    CompanyInitials = '';
    CompanyTypeTag: string | null = null;

    /** The company-description section heading, computed once after `Load()` (no template-bound call). */
    AboutHeading = AboutCompanyHeading(null);

    /** The Salary fact tile's value, computed once after `Load()` — not a template getter (ui-best-practices §7). */
    SalaryLine = 'Salary not stated';

    /** The salary tile's label, computed once after `Load()` alongside `SalaryLine` — see `SalaryFactLabel`. */
    SalaryLabel = SalaryFactLabel(null);

    /** The Work location tile's value, computed once after `Load()` — see `FormatWorkLocation`. */
    WorkLocationLine = FormatWorkLocation(null);

    /** "Show more" on the company-description clamp; `aria-expanded` follows it. */
    AboutExpanded = false;

    /**
     * Whether the 4-line clamp actually hides text — the toggle renders only then. A short
     * description fits in 4 lines, and a "Show more" there did nothing (Task 9 browser finding).
     * Set from a ResizeObserver on the paragraph, so a narrower viewport that starts clipping
     * the text brings the toggle back.
     */
    AboutClamped = false;

    private aboutObserver: ResizeObserver | null = null;

    /**
     * Each mounted paragraph gets its own ResizeObserver, whose initial observation measures it.
     * A reload always remounts the paragraph (`loading = true` swaps the whole view for the
     * progress bar), so a new job's description is measured without any extra re-measure step.
     */
    @ViewChild('aboutCo')
    set AboutParagraph(ref: ElementRef<HTMLElement> | undefined) {
        this.aboutObserver?.disconnect();
        this.aboutObserver = null;
        if (!ref || typeof ResizeObserver === 'undefined') return;
        const el = ref.nativeElement;
        this.aboutObserver = new ResizeObserver(() => this.MeasureAbout(el));
        this.aboutObserver.observe(el);
    }

    /** Benefit chips (global-constraints "Benefits") — `BenefitsAll` backs the "+N more" expansion, `BenefitsVisible` is the capped default. */
    BenefitsAll: string[] = [];
    BenefitsVisible: string[] = [];
    BenefitsMore = 0;
    ShowAllBenefits = false;

    /** The apply area (global-constraints "Apply links") — the server has already ordered/deduped/capped these. */
    PrimaryApply: ResearchApplyLink | null = null;
    OtherApply: ResearchApplyLink[] = [];

    private stateSub: Subscription | null = null;

    /** Latest-load-wins: only the newest JobID's response may write state or clear `loading`. */
    private readonly request = new LatestRequest<string>();

    constructor(
        private readonly api: ResearchApiService,
        private readonly itemState: MemberItemStateService,
        private readonly cdr: ChangeDetectorRef,
        /** The phone shell's toast, surface-provided (P/Member_Hub.html:953-958); absent outside a surface. */
        @Optional() @Inject(AGUToastService) private readonly toast: AGUToastService | null = null,
        /** The surface's host, composition and crumb service, for the crumb trail (PD §6); absent outside a surface. */
        @Optional() host: ExplorerRaHostAdapter | null = null,
        @Optional() composition: RaCompositionState | null = null,
        @Optional() @Inject(AGUCrumbService) crumbs: AGUCrumbService | null = null,
        /** The memoised portal config, for the error panel's Contact support address (PD §5); absent outside a surface. */
        @Optional() @Inject(PortalConfigService) private readonly portalConfig: PortalConfigService | null = null
    ) {
        this.Crumb = new DetailCrumb({ ModuleKey: RA_MODULE_KEYS.Jobs, View: 'detail', DefaultView: 'search' }, host, composition, crumbs);
    }

    ngOnInit(): void {
        this.Crumb.Start();
        void this.failures.ReadSupport(this.portalConfig);
        this.stateSub = this.itemState.Changes$.subscribe((change) => this.OnItemStateChanged(change));
        this.LoadIfChanged();
    }

    ngOnChanges(): void {
        // A JobID change while this component stays mounted reloads — whether the
        // previous load found the job, found nothing, or failed. ngOnInit also calls
        // LoadIfChanged, and HasChanged makes the repeat call a no-op.
        this.LoadIfChanged();
    }

    private LoadIfChanged(): void {
        if (this.request.HasChanged(this.JobID)) {
            void this.Load();
        }
    }

    ngOnDestroy(): void {
        this.Crumb.Stop();
        this.stateSub?.unsubscribe();
        this.aboutObserver?.disconnect();
    }

    private async Load(key: string = this.JobID): Promise<void> {
        this.loading = true;
        this.error = null;
        // A stale "Show more" from the previous job must not flash; the new paragraph's observer re-measures.
        this.AboutClamped = false;
        // The response is mapped to its view-model inside the fetch, so a mapper throw settles `failed`.
        const outcome = await this.request.Run(key, async (id): Promise<JobView> => this.ToView(await this.api.JobDetail(id)));
        // A newer JobID owns the view (and its loading flag) now — drop this response.
        if (outcome.Status === 'stale') return;
        this.loading = false;
        if (outcome.Status === 'failed') {
            this.error = DescribeError(outcome.Error);
            this.failures.Record(outcome.Error);
            return;
        }
        this.failures.Reset();
        const view = outcome.Value;
        this.job = view.job;
        this.PostedLine = view.PostedLine;
        this.SalaryLine = view.SalaryLine;
        this.SalaryLabel = view.SalaryLabel;
        this.WorkLocationLine = view.WorkLocationLine;
        this.AboutHeading = view.AboutHeading;
        this.DescriptionParagraphs = view.DescriptionParagraphs;
        this.LogoUrl = view.LogoUrl;
        this.CompanyInitials = view.CompanyInitials;
        this.CompanyTypeTag = view.CompanyTypeTag;
        this.AboutExpanded = false;
        this.BenefitsAll = view.BenefitsAll;
        this.BenefitsVisible = view.BenefitsVisible;
        this.BenefitsMore = view.BenefitsMore;
        this.ShowAllBenefits = false;
        this.PrimaryApply = view.PrimaryApply;
        this.OtherApply = view.OtherApply;
        if (view.job) {
            this.itemState.Prime(JOB_KIND, [{ key: view.job.jobId, itemState: view.job.itemState }]);
            this.Crumb.Loaded(key, view.job.title);
        }
    }

    private ToView(result: JobDetailComponent['job']): JobView {
        // `shown: benefits.length` dedupes/sentence-cases the WHOLE list once; `BenefitsVisible`
        // is just its first 10 — avoids running `BenefitChips` twice for the same result.
        const chips = result ? BenefitChips(result.benefits, result.benefits.length) : { Visible: [], More: 0 };
        return {
            job: result,
            PostedLine: result ? FormatJobPostedFact(result) : 'Posted date not stated',
            SalaryLine: result ? FormatSalaryLine(result) : 'Salary not stated',
            SalaryLabel: SalaryFactLabel(result ? result.salaryIsEstimated : null),
            WorkLocationLine: FormatWorkLocation(result ? result.remote : null),
            AboutHeading: AboutCompanyHeading(result ? result.companyName : null),
            DescriptionParagraphs: result ? SplitParagraphs(result.description ?? result.descriptionSnippet) : [],
            LogoUrl: result ? result.companyLogoUrl : null,
            CompanyInitials: Initials(result ? result.companyName : null),
            CompanyTypeTag: result ? CompanyTypeLabel(result.companyType) : null,
            BenefitsAll: chips.Visible,
            BenefitsVisible: chips.Visible.slice(0, BENEFITS_SHOWN),
            BenefitsMore: Math.max(0, chips.Visible.length - BENEFITS_SHOWN),
            PrimaryApply: result && result.applyLinks.length > 0 ? result.applyLinks[0] : null,
            OtherApply: result ? result.applyLinks.slice(1) : [],
        };
    }

    private OnItemStateChanged(change: MemberItemStateChange): void {
        if (change.kind !== JOB_KIND || !this.job || change.key !== this.job.jobId) return;
        this.job = { ...this.job, itemState: change.itemState };
    }

    Retry(): void {
        void this.Load(this.request.RequestedKey ?? this.JobID);
    }

    /** QA 2026-10-05: "Recommended" means matched to the member's profile — never pinned, never merely new (new has its own pill). */
    get IsRecommended(): boolean {
        return !!this.job && this.Recommended;
    }

    /**
     * `posted: 'none'` — Posted is already its own fact tile on this
     * view (see the template's "Posted" `.ra-dv__fact`), so the subtitle
     * line only needs company · location. Shares `FormatJobMeta` with
     * `JobCardComponent` (`posted: 'compact'` there) rather than
     * re-deriving the same company/location join (review finding,
     * 2026-09-03).
     */
    get MetaLine(): string | null {
        if (!this.job) return null;
        const meta = FormatJobMeta(this.job, { posted: 'none' });
        return meta.length > 0 ? meta : null;
    }

    async OnSaveToggle(): Promise<void> {
        if (!this.job) return;
        const saved = !this.job.itemState.saved;
        await this.itemState.Toggle(JOB_KIND, this.job.jobId, { saved });
        this.toast?.ShowSaved(saved); // The bookmark toast (P/Member_Hub.html:953-966, `#saveBtn` :960), once the save has stuck.
    }

    async OnPinToggle(): Promise<void> {
        if (!this.job) return;
        await this.itemState.Toggle(JOB_KIND, this.job.jobId, { pinned: !this.job.itemState.pinned });
    }

    /** The header logo `<img>`'s `(error)` handler — swaps to initials once, never retries the same broken url (mirrors `JobCardComponent.OnLogoError`). */
    OnLogoError(): void {
        this.LogoUrl = null;
    }

    OnToggleAbout(): void {
        this.AboutExpanded = !this.AboutExpanded;
    }

    /**
     * Re-reads the clamp. Expanded, the paragraph never overflows, so the last clamped
     * answer stands — the toggle stays put to offer "Show less".
     */
    MeasureAbout(el: HTMLElement): void {
        if (this.AboutExpanded) return;
        const clamped = el.scrollHeight > el.clientHeight + 1;
        if (clamped === this.AboutClamped) return;
        this.AboutClamped = clamped;
        this.cdr.markForCheck();
    }

    /**
     * A toggle, not a one-way reveal — the button stays rendered (and
     * focused) across the flip, labelled "Show fewer" / `aria-expanded` when
     * open, rather than being removed from the DOM and dropping focus
     * (fix-round 1 finding #3).
     */
    OnToggleBenefits(): void {
        this.ShowAllBenefits = !this.ShowAllBenefits;
    }
}
